import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { EMPTY, Observable, Subject, of, throwError } from 'rxjs';
import { catchError, map, shareReplay, tap, timeout } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { AuthService } from '../auth/auth.service';
import {
  EMPTY_RBAC_CONTEXT,
  RbacContext,
} from './rbac.models';
import { RbacSummaryResponse } from '../../user-management/role-permission-management/role-permission-management.models';
import {
  RBAC_DEFAULT_ROUTE_ORDER,
  RBAC_ROUTE_PERMISSIONS,
} from './rbac-route-map';
import {
  LEGACY_PERMISSION_GRANTS,
  PERMISSION_ALIASES,
} from './rbac-permission-grants';
import { RbacPermissions } from './rbac-permissions';
import {
  LoggedInResellerInfo,
  RBAC_ROLE_TOKENS,
  ResellerIdentifiable,
  UserRoleAssignmentViewer,
  canAssignUserRole,
  hasAnyNormalizedRole,
  resolveResellerRecordId,
} from './rbac-role.helpers';

@Injectable({
  providedIn: 'root',
})
export class RbacService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);
  /** BE may expose GET /rbac/context or GET /rbac — try both. */
  private readonly rbacUrls = [
    `${environment.userServiceUrl}/rbac/context`,
    `${environment.userServiceUrl}/rbac`,
  ];

  private context: RbacContext = { ...EMPTY_RBAC_CONTEXT };
  private loadRequest$: Observable<RbacContext> | null = null;
  private cachedToken: string | null = null;
  private loadFailed = false;
  private contextReady = false;
  private readonly contextChanged = new Subject<RbacContext>();
  /** Emits whenever RBAC context is loaded or refreshed. */
  readonly contextChanges$ = this.contextChanged.asObservable();

  /**
   * Emits when roles/permissions change so summary cards (Total Roles /
   * Total Permissions) can reload without a full page refresh.
   */
  private readonly summaryInvalidated = new Subject<void>();
  readonly summaryInvalidated$ = this.summaryInvalidated.asObservable();

  private static readonly LOAD_TIMEOUT_MS = 8000;

  /** Call after create/update/delete of roles or permissions. */
  invalidateSummary(): void {
    this.summaryInvalidated.next();
  }

  getRbacSummary(): Observable<RbacSummaryResponse> {
    return this.http
      .get<RbacSummaryResponse | { data?: RbacSummaryResponse }>(
        `${environment.userServiceUrl}/rbac/summary`
      )
      .pipe(map((response) => this.unwrapRbacSummary(response)));
  }

  ensureContextLoaded(): Observable<RbacContext> {
    const token = this.authService.getToken();
    if (!token) {
      this.clearContext();
      return of(this.context);
    }

    if (token !== this.cachedToken) {
      this.clearContext();
      this.cachedToken = token;
    }

    if (this.loadRequest$) {
      return this.loadRequest$;
    }

    this.loadRequest$ = this.fetchContextFromApi().pipe(
      timeout(RbacService.LOAD_TIMEOUT_MS),
      tap((ctx) => this.applyContext(ctx, false)),
      catchError((error) => {
        // 401/440 is an expired or rejected session, not "this user has no pages".
        // Publishing an empty context here is what left production on a blank shell.
        if (this.isUnauthorized(error)) {
          this.authService.handleUnauthorizedResponse();
          return EMPTY;
        }
        console.warn(
          'RBAC context could not be loaded; protected routes will be denied.',
          error
        );
        this.applyContext({ ...EMPTY_RBAC_CONTEXT }, true);
        return of(this.context);
      }),
      shareReplay(1)
    );

    return this.loadRequest$;
  }

  clearContext(): void {
    this.context = { ...EMPTY_RBAC_CONTEXT };
    this.loadRequest$ = null;
    this.cachedToken = null;
    this.loadFailed = false;
    this.contextReady = false;
    this.contextChanged.next(this.context);
  }

  hasAnyAccessibleRoute(): boolean {
    return this.getAccessibleRoutes().length > 0;
  }

  getAccessibleRoutes(): string[] {
    return Object.keys(RBAC_ROUTE_PERMISSIONS).filter((route) =>
      this.canAccessRoute(route)
    );
  }

  isContextLoadFailed(): boolean {
    return this.loadFailed;
  }

  isContextReady(): boolean {
    return this.contextReady;
  }

  /**
   * True only when the loaded context was fetched for the token currently in storage.
   * Another tab can sign out / sign in a different user, replacing the shared token
   * while this tab still holds the previous user's permissions in memory.
   */
  isContextReadyForCurrentSession(): boolean {
    return (
      this.contextReady &&
      this.cachedToken !== null &&
      this.cachedToken === this.authService.getToken()
    );
  }

  getContext(): RbacContext {
    return this.context;
  }

  isSuperAdmin(): boolean {
    return this.context.superAdmin === true;
  }

  isResellerAdmin(): boolean {
    return hasAnyNormalizedRole(
      this.context.roleNames,
      RBAC_ROLE_TOKENS.RESELLER_ADMIN
    );
  }

  isResellerUser(): boolean {
    return hasAnyNormalizedRole(
      this.context.roleNames,
      RBAC_ROLE_TOKENS.RESELLER_USER
    );
  }

  /** Reseller Admin or Reseller User — scoped customer creation. */
  isResellerRole(): boolean {
    return this.isResellerAdmin() || this.isResellerUser();
  }

  isCustomerAdmin(): boolean {
    return hasAnyNormalizedRole(
      this.context.roleNames,
      RBAC_ROLE_TOKENS.CUSTOMER_ADMIN
    );
  }

  isCustomerUser(): boolean {
    return hasAnyNormalizedRole(
      this.context.roleNames,
      RBAC_ROLE_TOKENS.CUSTOMER_USER
    );
  }

  isCustomerRole(): boolean {
    return this.isCustomerAdmin() || this.isCustomerUser();
  }

  /** Platform Admin (not Super Admin, reseller, or customer scoped roles). */
  isAdmin(): boolean {
    if (this.isSuperAdmin()) {
      return false;
    }
    if (this.isResellerRole() || this.isCustomerRole()) {
      return false;
    }
    return hasAnyNormalizedRole(
      this.context.roleNames,
      RBAC_ROLE_TOKENS.ADMIN
    );
  }

  getUserRoleAssignmentViewer(): UserRoleAssignmentViewer {
    return {
      isSuperAdmin: this.isSuperAdmin(),
      isPlatformAdmin: this.isAdmin(),
      isResellerAdmin: this.isResellerAdmin(),
      isResellerUser: this.isResellerUser(),
      isCustomerAdmin: this.isCustomerAdmin(),
      isCustomerUser: this.isCustomerUser(),
    };
  }

  canAssignUserRole(roleName: string): boolean {
    return canAssignUserRole(roleName, this.getUserRoleAssignmentViewer());
  }

  getAdministeredCustomerIds(): Array<number | string> {
    return [...this.context.administeredCustomerIds];
  }

  /**
   * Resolves the logged-in user's parent reseller from an API reseller list
   * and RBAC administeredCustomerIds.
   */
  getLoggedInResellerInfo(
    resellers: ResellerIdentifiable[]
  ): LoggedInResellerInfo | null {
    const match = this.resolveLoggedInResellerRecord(resellers);
    if (!match) {
      return null;
    }
    const id = resolveResellerRecordId(match);
    if (id == null) {
      return null;
    }
    return {
      id,
      customerName: match.customerName ?? '',
      accountNumber: match.accountNumber,
    };
  }

  private resolveLoggedInResellerRecord(
    resellers: ResellerIdentifiable[]
  ): ResellerIdentifiable | null {
    if (!this.isResellerRole() || !resellers.length) {
      return null;
    }

    const administered = new Set(
      this.getAdministeredCustomerIds().map(String)
    );

    if (resellers.length === 1) {
      const single = resellers[0];
      return resolveResellerRecordId(single) != null ? single : null;
    }

    const matched = resellers.find((reseller) => {
      const id = resolveResellerRecordId(reseller);
      return id != null && administered.has(String(id));
    });
    if (matched) {
      return matched;
    }

    if (administered.size === 1) {
      const targetId = [...administered][0];
      return (
        resellers.find((reseller) => {
          const id = resolveResellerRecordId(reseller);
          return id != null && String(id) === targetId;
        }) ?? null
      );
    }

    return null;
  }

  /**
   * UI/route gating uses superAdmin + permissions[] only.
   * fullAccess and roleNames (e.g. Customer_Admin) are never used as bypasses.
   */
  hasDeviceModuleAccess(): boolean {
    if (this.isSuperAdmin()) {
      return true;
    }
    return this.hasPermission(RbacPermissions.DeviceList_View);
  }

  /**
   * Apply RBAC from login/authenticate payload when BE embeds context in the response.
   * GET /rbac/context still runs afterward as the source of truth.
   */
  seedFromAuthPayload(payload: unknown): void {
    if (!this.hasRbacFields(payload)) {
      return;
    }
    this.applyContext(this.normalizeContext(payload), false);
  }

  hasPermission(permission: string): boolean {
    if (!permission) {
      return false;
    }
    if (this.isSuperAdmin()) {
      return true;
    }
    if (this.context.permissions.includes(permission)) {
      return true;
    }
    const aliasKeys = PERMISSION_ALIASES[permission] ?? [permission];
    if (aliasKeys.some((key) => this.context.permissions.includes(key))) {
      return true;
    }
    return this.isGrantedByLegacyPermission(permission);
  }

  private isGrantedByLegacyPermission(permission: string): boolean {
    for (const [legacyPermission, grantedPermissions] of Object.entries(
      LEGACY_PERMISSION_GRANTS
    )) {
      if (
        this.context.permissions.includes(legacyPermission) &&
        grantedPermissions.includes(permission)
      ) {
        return true;
      }
    }
    return false;
  }

  hasAnyPermission(permissions: readonly string[]): boolean {
    if (!permissions?.length) {
      return false;
    }
    if (this.isSuperAdmin()) {
      return true;
    }
    return permissions.some((p) => this.hasPermission(p));
  }

  hasPermissionPrefix(prefix: string): boolean {
    if (!prefix) {
      return false;
    }
    if (this.isSuperAdmin()) {
      return true;
    }
    return this.context.permissions.some((p) => p.startsWith(prefix));
  }

  canAccessRoute(url: string): boolean {
    const path = this.normalizeRoutePath(url);
    const required = this.resolveRoutePermissions(path);
    if (!required.length) {
      return true;
    }
    if (this.loadFailed) {
      return false;
    }
    return this.hasAnyPermission(required);
  }

  getDefaultRoute(): string {
    const ordered = [
      ...RBAC_DEFAULT_ROUTE_ORDER,
      ...Object.keys(RBAC_ROUTE_PERMISSIONS).filter(
        (route) => !RBAC_DEFAULT_ROUTE_ORDER.includes(route)
      ),
    ];
    const seen = new Set<string>();
    for (const route of ordered) {
      if (seen.has(route)) {
        continue;
      }
      seen.add(route);
      if (this.canAccessRoute(route)) {
        return route;
      }
    }
    return '/login';
  }

  private normalizeRoutePath(url: string): string {
    const path = url.split('?')[0];
    if (path.startsWith('/device-details')) {
      return '/device-details';
    }
    return path;
  }

  private resolveRoutePermissions(path: string): string[] {
    if (RBAC_ROUTE_PERMISSIONS[path]) {
      return RBAC_ROUTE_PERMISSIONS[path];
    }
    for (const [route, permissions] of Object.entries(RBAC_ROUTE_PERMISSIONS)) {
      if (path.startsWith(route + '/')) {
        return permissions;
      }
    }
    return [];
  }

  private fetchContextFromApi(): Observable<RbacContext> {
    const [primaryUrl, ...fallbackUrls] = this.rbacUrls;
    return this.http.get<unknown>(primaryUrl).pipe(
      map((response) => this.normalizeContext(response)),
      catchError((error) => {
        if (this.isUnauthorized(error)) {
          return throwError(() => error);
        }
        return this.tryFallbackRbacUrls(fallbackUrls);
      })
    );
  }

  private tryFallbackRbacUrls(urls: string[]): Observable<RbacContext> {
    if (!urls.length) {
      return of({ ...EMPTY_RBAC_CONTEXT });
    }
    const [nextUrl, ...rest] = urls;
    return this.http.get<unknown>(nextUrl).pipe(
      map((response) => this.normalizeContext(response)),
      catchError((error) => {
        if (this.isUnauthorized(error)) {
          return throwError(() => error);
        }
        return this.tryFallbackRbacUrls(rest);
      })
    );
  }

  private isUnauthorized(error: unknown): boolean {
    return (
      error instanceof HttpErrorResponse &&
      (error.status === 401 || error.status === 440)
    );
  }

  private applyContext(ctx: RbacContext, failed: boolean): void {
    this.context = ctx;
    this.loadFailed = failed;
    this.contextReady = true;
    this.contextChanged.next(ctx);
  }

  private normalizeContext(response: unknown): RbacContext {
    const raw = this.unwrapContextPayload(response);
    return {
      superAdmin: raw['superAdmin'] === true,
      fullAccess: raw['fullAccess'] === true,
      permissions: this.normalizePermissionList(raw['permissions']),
      roleNames: Array.isArray(raw['roleNames'])
        ? raw['roleNames'].filter((item): item is string => typeof item === 'string')
        : [],
      administeredCustomerIds: Array.isArray(raw['administeredCustomerIds'])
        ? [...raw['administeredCustomerIds']]
        : [],
    };
  }

  private unwrapContextPayload(response: unknown): Record<string, unknown> {
    if (!response || typeof response !== 'object') {
      return {};
    }
    const record = response as Record<string, unknown>;
    const nested =
      record['data'] ?? record['result'] ?? record['rbac'] ?? record['context'];
    if (nested && typeof nested === 'object') {
      return nested as Record<string, unknown>;
    }
    return record;
  }

  private hasRbacFields(payload: unknown): boolean {
    if (!payload || typeof payload !== 'object') {
      return false;
    }
    const record = payload as Record<string, unknown>;
    const nested = record['data'] ?? record['rbac'] ?? record['context'];
    const source =
      nested && typeof nested === 'object'
        ? (nested as Record<string, unknown>)
        : record;
    return (
      Array.isArray(source['permissions']) ||
      source['superAdmin'] === true
    );
  }

  private normalizePermissionList(value: unknown): string[] {
    if (Array.isArray(value)) {
      return value.filter((item): item is string => typeof item === 'string');
    }
    if (typeof value === 'string' && value.trim()) {
      return value
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    }
    return [];
  }

  private unwrapRbacSummary(
    response: RbacSummaryResponse | { data?: RbacSummaryResponse }
  ): RbacSummaryResponse {
    if ('data' in response && response.data) {
      return response.data;
    }
    return response as RbacSummaryResponse;
  }
}
